import { supabase } from '../../utils/supabase';
import { PlayerInfo, ScoreCalculation } from './types';
import { VictoryType, Faction } from '../../types/stats';
import { statsDataService } from './dataService';
import { statsCache } from '../../utils/statsCache';
import { processRecordStorage } from '../../utils/processRecordStorage';

export interface GameData {
  playerCount: number;
  winningFaction: Faction;
  victoryType: VictoryType;
  taskResults: (boolean | null)[];
  goodMvp: number | null;
  evilMvp: number | null;
  selfDestruct: number | null;
  wrongKill: number | null;
  bugPlayer: number | null;
  gameDate: string;
  gameNumber: number;
  dojoId: string;
  seasonId: string;
}

export class GameService {
  private clearPlayerStatsCache(dojoId?: number, seasonId?: number) {
    const specificKey = dojoId !== undefined && seasonId !== undefined
      ? `player_stats:${dojoId}:${seasonId}`
      : null;

    if (specificKey) {
      statsCache.remove(specificKey);
    }

    statsCache.remove('player_stats:all:all');

    ['player_stats:1:1', 'player_stats:1:2', 'player_stats:2:1', 'player_stats:2:2'].forEach((key) => {
      statsCache.remove(key);
    });
  }

  async saveGameRecord(
    gameData: GameData,
    playerInfos: PlayerInfo[],
    calculatedScores: ScoreCalculation[],
    editingGameId: number | null
  ): Promise<void> {
    try {
      const dbGameData: any = {
        player_count: gameData.playerCount,
        winning_faction: gameData.winningFaction,
        victory_type: gameData.victoryType,
        task_results: gameData.taskResults,
        good_mvp_player: gameData.goodMvp,
        evil_mvp_player: gameData.evilMvp,
        self_destruct_player: gameData.selfDestruct,
        wrong_kill_player: gameData.wrongKill,
        bug_player: gameData.bugPlayer,
        game_date: gameData.gameDate,
        dojo_id: gameData.dojoId ? Number(gameData.dojoId) : null,
        season_id: gameData.seasonId ? Number(gameData.seasonId) : null,
        game_number: gameData.gameNumber,
        status: 'valid',
      };

      let gameRecord: any;

      if (editingGameId) {
        await statsDataService.invalidateGameRecords(editingGameId);
      }

      const { data, error } = await supabase
        .from('game_records')
        .insert(dbGameData)
        .select()
        .single();

      if (error) throw error;
      gameRecord = data;

      const roundRecords = calculatedScores.map((score) => {
        const playerInfo = playerInfos.find((p) => p.player_number === score.player_number);
        const missionResults = gameData.taskResults.filter((r) => r !== null);
        const successCount = missionResults.filter((r) => r === true).length;
        const failCount = missionResults.filter((r) => r === false).length;

        return {
          game_record_id: gameRecord.id,
          player_number: score.player_number,
          player_nickname: playerInfo?.player_nickname || `${score.player_number}号`,
          player_role: playerInfo?.player_role || '',
          faction: score.faction,
          team_type: score.faction === 'good' ? '正义' : '邪恶',
          score_change: score.total_score,
          is_mvp: score.is_mvp,
          is_good_mvp: score.is_good_mvp,
          is_evil_mvp: score.is_evil_mvp,
          has_penalty: score.penalty < 0,
          penalty_type:
            score.penalty < 0
              ? score.player_number === gameData.selfDestruct
                ? 'self_destruct'
                : score.player_number === gameData.wrongKill
                  ? 'wrong_kill'
                  : score.player_number === gameData.bugPlayer
                    ? 'bug'
                    : null
              : null,
          penalty_score: score.penalty,
          mission_result: `${successCount}胜${failCount}负`,
          assassin_attempt:
            gameData.victoryType === 'assassin_success'
              ? '刺杀梅林成功'
              : gameData.victoryType === 'assassin_fail'
                ? '提前刺杀失败'
                : '',
          status: 'valid',
        };
      });

      const { error: insertError } = await supabase.from('player_round_records').insert(roundRecords);
      if (insertError) throw insertError;

      if (editingGameId) {
        const reboundLabel = `${gameData.gameDate || ''} 第${gameData.gameNumber || 1}局`.trim();
        processRecordStorage.rebindDraftForGame(editingGameId, gameRecord.id, reboundLabel);
      }

      this.clearPlayerStatsCache(
        gameData.dojoId ? Number(gameData.dojoId) : undefined,
        gameData.seasonId ? Number(gameData.seasonId) : undefined
      );
    } catch (err) {
      console.error('[GameService] saveGameRecord failed:', err);
      throw err;
    }
  }

  async deleteGameRecord(gameId: number): Promise<void> {
    try {
      await statsDataService.invalidateGameRecords(gameId);
      this.clearPlayerStatsCache();
    } catch (err: any) {
      console.error('[GameService] deleteGameRecord failed:', err);
      throw err;
    }
  }
}

export const gameService = new GameService();
